import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { SideOverlay } from "../../../components";
import { useStore } from "../../../helpers";
import { useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { FTPInput, FTPSelect, FTPCheckBox, FTPDateTimePicker } from "../../../components-new";
import { Button } from "@mui/material";
import { useCreateWebLink, useUpdateWebLink } from "../hooks/useWebLinkQueries";
import { useGetFullProject } from "../../../custom-hooks/useGetFullProject";
import { toast } from "react-toastify";
import { SurveyorAutoComplete } from "./SurveyorAutoComplete";
import { formatDate } from "../utils/weblink-utils";

interface FormData {
    surveyid: string;
    surveyorid: any; // Will store user object from autocomplete
    active_from: string;
    active_until: string;
    maxlimit: string;
    title: string;
    active: boolean;
}

export const WebLinkModal = observer(() => {
    const { activeSurveyStore } = useStore();
    const { projectid } = useParams<{ projectid: string }>();
    const activeWebLink = activeSurveyStore.activeWebLink;

    const isEditMode = !!activeWebLink;
    const isCreateModalOpen = activeSurveyStore.createWebLinkModalOpen;
    const isEditModalOpen = activeSurveyStore.editWebLinkModalOpen;
    const isOpen = isCreateModalOpen || isEditModalOpen;

    const { data: projectData } = useGetFullProject();
    const createWebLinkMutation = useCreateWebLink();
    const updateWebLinkMutation = useUpdateWebLink();

    const methods = useForm<FormData>({
        defaultValues: {
            surveyid: "",
            surveyorid: null,
            active_from: "",
            active_until: "",
            maxlimit: "1",
            title: "",
            active: true,
        },
    });

    const surveys = useMemo(() => {
        if (projectData && (projectData as any)?.surveys) {
            return (projectData as any).surveys;
        }
        return [];
    }, [projectData]);

    // Populate form when editing
    useEffect(() => {
        if (isEditMode && activeWebLink) {
            methods.reset({
                surveyid: activeWebLink.surveyid.toString(),
                surveyorid: activeWebLink.surveyorid ? { id: activeWebLink.surveyorid } : null,
                active_from: activeWebLink.active_from || "",
                active_until: activeWebLink.active_until || "",
                maxlimit: activeWebLink.maxlimit.toString(),
                title: activeWebLink.title || "",
                active: activeWebLink.active,
            });
        } else if (!isEditMode) {
            // Reset to defaults when creating
            methods.reset({
                surveyid: "",
                surveyorid: null,
                active_from: "",
                active_until: "",
                maxlimit: "1",
                title: "",
                active: true,
            });
        }
    }, [activeWebLink, isEditMode, methods]);

    const onSubmit = (data: FormData) => {
        if (isEditMode) {
            // Edit mode
            if (!activeWebLink) return;

            const updates: any = {};

            if (data.surveyid) updates.surveyid = parseInt(data.surveyid);
            if (data.title !== undefined) updates.title = data.title || null;
            // Extract user ID from autocomplete selection
            if (data.surveyorid) {
                const surveyorId = typeof data.surveyorid === "object" ? data.surveyorid.id : data.surveyorid;
                if (surveyorId) updates.surveyorid = typeof surveyorId === "string" ? parseInt(surveyorId) : surveyorId;
            } else {
                updates.surveyorid = null;
            }
            if (data.active_from) {
                const date = new Date(data.active_from);
                updates.active_from = date.toISOString();
            } else {
                updates.active_from = null;
            }
            if (data.active_until) {
                const date = new Date(data.active_until);
                updates.active_until = date.toISOString();
            } else {
                updates.active_until = null;
            }
            if (data.maxlimit) updates.maxlimit = parseInt(data.maxlimit);
            updates.active = data.active;

            updateWebLinkMutation.mutate({
                id: activeWebLink.id,
                updates,
            });
        } else {
            // Create mode
            if (!projectid) {
                toast.error("Project ID is required");
                return;
            }
            if (!data.surveyid) {
                toast.error("Survey is required");
                methods.setError("surveyid", { message: "Survey is required" });
                return;
            }

            const payload: any = {
                projectid: parseInt(projectid),
                surveyid: parseInt(data.surveyid),
                maxlimit: parseInt(data.maxlimit) || 1,
                active: data.active,
            };

            if (data.title) payload.title = data.title;
            // Extract user ID from autocomplete selection
            if (data.surveyorid) {
                const surveyorId = typeof data.surveyorid === "object" ? data.surveyorid.id : data.surveyorid;
                if (surveyorId) payload.surveyorid = typeof surveyorId === "string" ? parseInt(surveyorId) : surveyorId;
            }
            if (data.active_from) {
                // Convert to ISO8601 format
                const date = new Date(data.active_from);
                payload.active_from = date.toISOString();
            }
            if (data.active_until) {
                // Convert to ISO8601 format
                const date = new Date(data.active_until);
                payload.active_until = date.toISOString();
            }

            createWebLinkMutation.mutate(payload, {
                onSuccess: () => {
                    methods.reset();
                },
            });
        }
    };

    const handleClose = () => {
        if (isEditMode) {
            activeSurveyStore.setEditWebLinkModalOpen(false);
            activeSurveyStore.setActiveWebLink(null);
        } else {
            activeSurveyStore.setCreateWebLinkModalOpen(false);
        }
        methods.reset();
    };

    const surveyOptions = surveys.map((survey) => ({
        label: survey.name,
        value: survey.id.toString(),
    }));

    const isLoading = isEditMode
        ? updateWebLinkMutation.isLoading
        : createWebLinkMutation.isLoading;

    const modalTitle = isEditMode
        ? "Edit Survey Interview Link"
        : "Create Survey Interview Link";

    const submitButtonText = isEditMode
        ? (isLoading ? "Updating..." : "Update")
        : (isLoading ? "Creating..." : "Create");

    // Don't render if in edit mode but no activeWebLink
    if (isEditMode && !activeWebLink) return null;

    return (
        <SideOverlay
            show={isOpen}
            onClose={handleClose}
            title={modalTitle}
        >
            {isEditMode && activeWebLink && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                        <span className="font-medium">GUID:</span> {activeWebLink.guid}
                    </p>
                    <p className="text-sm text-gray-600">
                        <span className="font-medium">Created:</span> {formatDate(activeWebLink.created_at)}
                    </p>
                    <p className="text-sm text-gray-600">
                        <span className="font-medium">Responses:</span> {activeWebLink.srids_count} / {activeWebLink.maxlimit}
                    </p>
                </div>
            )}

            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
                    <FTPInput
                        name="title"
                        label="Title"
                        maxLength={255}
                        placeholder="Optional title for this link"
                    />
                    <FTPSelect
                        name="surveyid"
                        label="Survey"
                        options={surveyOptions}
                    />
                    <SurveyorAutoComplete
                        name="surveyorid"
                        label="Surveyor"
                        placeholder="Search and select surveyor"
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <FTPDateTimePicker
                            name="active_from"
                            label="Active From"
                            placeholder="Select date and time"
                        />
                        <FTPDateTimePicker
                            name="active_until"
                            label="Active Until"
                            placeholder="Select date and time"
                        />
                    </div>
                    <FTPInput
                        name="maxlimit"
                        label="Max Limit"
                        type="number"
                        inputProps={{ min: 1 }}
                    />
                    <FTPCheckBox name="active" label="Active" />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ minWidth: 100 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading}
                            sx={{ minWidth: 100 }}
                        >
                            {submitButtonText}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </SideOverlay>
    );
});

